import { useRecordContext, ReferenceField, TextField } from "react-admin";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip, type StatusChipColor } from "@/components/common/fields/StatusChipField";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { ResourceName } from "@/types";
import {
  DETECTION_REASON_LABELS,
  DETECTION_SOURCE_LABELS,
  DIFFERING_FIELD_LABELS,
  DUPLICATE_PAIR_STATUS_LABELS,
  type DuplicateClaimantPair,
  type DuplicatePairSeverity,
  type DuplicatePairStatus,
} from "@/types/duplicateClaimantPair";

/**
 * Cell components for the duplicate claimant review list (#6717).
 *
 * Split out of the list file to match the house pattern set by
 * `claimants/components/ClaimantListCells.tsx` — the list declares columns, the
 * cells own their rendering, and each cell reads from `useRecordContext` rather
 * than taking a record prop so it can sit inside a `WrapperField`.
 */

// Uses the shared StatusChip rather than a raw MUI Chip. MUI's filled chips
// render solid, saturated pills; every status indicator in this app is a subtly
// tinted, dark-mode-aware StatusChip, so hand-rolled chips read as louder and
// visually foreign next to the rest of the platform.
//
// StatusChip carries no icon slot, so the severity warning glyph is dropped.
// That is the right trade: colour plus label already conveys severity, and
// matching the platform's chip language matters more than the decoration.
// Typed to the component's own union, not ColorType: ColorType includes
// Inherit, which StatusChip does not accept.
const SEVERITY_COLORS: Record<DuplicatePairSeverity, StatusChipColor> = {
  high: ColorType.Error,
  medium: ColorType.Warning,
  low: ColorType.Default,
};

const SEVERITY_LABELS: Record<DuplicatePairSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Amounts arrive as decimal STRINGS because JSON floats cannot represent them
 * exactly — they are compared against a specific deductible upstream. Parse only
 * here, at the display boundary, never for arithmetic.
 */
export const formatAmount = (value?: string): string => {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const SeverityCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;
  const color = SEVERITY_COLORS[record.severity] ?? ColorType.Default;
  const label = SEVERITY_LABELS[record.severity] ?? record.severity;

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {/* Fixed-width wrapper so the Re-raised chip begins at the same x on every row. */}
      <Box sx={{ width: 68, flexShrink: 0 }}>
        <StatusChip label={label} color={color} size={UIComponentSize.Small} />
      </Box>
      {/*
        A re-raised pair was dismissed before and returned because the evidence
        changed. Without this marker a reviewer re-dismisses on reflex and the
        fingerprint mechanism is wasted — exactly the trap a corrected DOB sets.
      */}
      {record.was_re_raised && (
        <Tooltip title="Previously dismissed — returned because the evidence changed. Re-check before dismissing again.">
          <Box component="span">
            <StatusChip label="Re-raised" color={ColorType.Info} size={UIComponentSize.Small} />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

// Resolved states are muted so the rows still needing a decision stand out when
// the Status facet is cleared or a URL filter has replaced the Open default.
export const STATUS_COLORS: Record<DuplicatePairStatus, StatusChipColor> = {
  open: ColorType.Info,
  confirmed: ColorType.Primary,
  dismissed_not_duplicate: "muted",
  merged: ColorType.Success,
  self_resolved: "muted",
};

export const StatusCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;
  return (
    <StatusChip
      label={DUPLICATE_PAIR_STATUS_LABELS[record.status] ?? record.status}
      color={STATUS_COLORS[record.status] ?? ColorType.Default}
      size={UIComponentSize.Small}
    />
  );
};

export const ClientCell = () => (
  <ReferenceField source="client_id" reference={ResourceName.CLIENTS} link="show" sortable={false}>
    <TextField source="client_legal_name" />
  </ReferenceField>
);

/**
 * Both sides in one cell. The reviewer decides from the row — having to open two
 * claimants and a policy is the friction that leaves a queue unworked.
 */
export const ClaimantPairCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;

  // ReferenceField resolves the claimant NAME, matching how ClaimantList renders
  // client_id_ref. A truncated UUID tells a reviewer nothing -- the entire premise
  // of this list is that the comparison is decidable from the row, and you cannot
  // judge whether two records are the same person without their names.
  //
  // Names are intentionally NOT on the pair API payload: #5898 keeps identifiers
  // and names out of anything log-shaped, so the SPA resolves them through the
  // claimants resource at render time instead.
  // A three-column grid, not a flex row. With flex, a long name ("Diana Marie
  // Thompson") wraps and shoves the space chip and amount out of line, so the two
  // sides of the pair no longer read as a comparison — which is the one job this
  // cell has. Fixed tracks keep chips and amounts in column across every row.
  const side = (source: "claimant_a_id" | "claimant_b_id", space?: string, amount?: string) => (
    <Box
      sx={{
        display: "grid",
        // Name track gets a real minimum: truncating a claimant name defeats the
        // cell's purpose, since the comparison IS the two names. The chip and
        // amount tracks stay fixed so both sides line up column-for-column.
        gridTemplateColumns: "minmax(190px, 1fr) 76px 96px",
        columnGap: 1,
        alignItems: "center",
        minWidth: 380,
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          "& a": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" },
        }}
      >
        <ReferenceField source={source} reference={ResourceName.CLAIMANTS} link="show" sortable={false}>
          <TextField source="claimant_name" />
        </ReferenceField>
      </Box>
      <Box>
        {/* Neutral tag, not a status — Default keeps it quiet so severity stays
            the only colour pulling the eye. */}
        {space && (
          <StatusChip
            label={space === "rx" ? "Rx" : "Medical"}
            color={ColorType.Default}
            size={UIComponentSize.Small}
          />
        )}
      </Box>
      {/* Right-aligned and tabular so the two amounts compare digit-for-digit. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
      >
        {formatAmount(amount)}
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={0.5}>
      {side("claimant_a_id", record.claimant_a_identifier_space, record.claimant_a_paid_amount)}
      {side("claimant_b_id", record.claimant_b_identifier_space, record.claimant_b_paid_amount)}
    </Stack>
  );
};

/**
 * Combined spend against the deductible — the whole materiality judgement.
 * Rendered as an amount over "of $X SD" so the proportion is legible without the
 * reviewer doing the division.
 */
export const CombinedSpendCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;

  return (
    <Stack spacing={0.25} alignItems="flex-end">
      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatAmount(record.combined_paid_amount)}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {record.specific_deductible ? `of ${formatAmount(record.specific_deductible)} SD` : "no deductible resolved"}
      </Typography>
    </Stack>
  );
};

export const WhyFlaggedCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;

  // Plain text, not chips. Chips here competed with the severity chip for
  // attention, and severity is the only thing that should pull the eye — it is
  // the triage axis. The evidence is supporting detail: a reviewer reads it after
  // deciding the row is worth looking at, so it belongs in quiet secondary text.
  const matched = record.matched_on ? `matched ${record.matched_on}` : null;
  const differs = record.differing_fields?.length
    ? `differs on ${record.differing_fields.map((f) => DIFFERING_FIELD_LABELS[f] ?? f).join(", ")}`
    : null;
  const detail = [matched, differs].filter(Boolean).join(" · ");

  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">
        {DETECTION_REASON_LABELS[record.detection_reason] ?? record.detection_reason}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.disabled">
          {detail}
        </Typography>
      )}
    </Stack>
  );
};

export const DetectionSourceCell = () => {
  const record = useRecordContext<DuplicateClaimantPair>();
  if (!record) return null;

  return (
    <Typography variant="body2" color="text.secondary">
      {DETECTION_SOURCE_LABELS[record.detection_source] ?? record.detection_source}
    </Typography>
  );
};
